import { PaletteColor, createTheme, TuscanyTheme } from '@design/pt-ui'

interface PaletteWithBackground extends PaletteColor {
  background: string
}

interface CustomPalette {
  button: PaletteColor
  gray: PaletteWithBackground
  success: PaletteColor
  border: {
    active: string
    inactive: string
    disabled: string
    recommended: string
  }
  statusIcon: {
    inProgress: string
    sent: string
    archived: string
    reopened: string
    finalized: string
    outdated: string
  }
  link: {
    main: string
    hover: string
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    custom: CustomPalette
  }
  interface PaletteOptions {
    custom?: CustomPalette
  }
}

// Built on pt-ui's Tuscany theme: we inherit its palette, typography, shadows
// and component overrides, then layer this app's own values on top.
//
// Still overridden here on purpose -- revisit each as we adopt more of pt-ui:
//   spacing      pt-ui uses MUI's default 8; this app is calibrated to 6.
//   typography   pt-ui uses a system font stack; this app uses helvetica.
//   palette      keeps this app's primary/error/warning plus `custom`, which
//                no pt-ui theme defines (see CustomPalette above).
// Tuscany ships a much flatter shadow ramp (elevation 1 is a single faint
// `0px 3px 2px -2px rgba(0,0,0,0.15)` vs MUI's three-layer shadow), which makes
// every <Paper> look like it lost its elevation. Keep MUI's default ramp.
const defaultShadows = createTheme().shadows

const themeData = createTheme(
  {
    // Spread + palette must live in the FIRST argument: MUI re-processes it
    // (spacing -> function, palette -> augmented). Later arguments are a raw
    // deepmerge, so `spacing: 6` there would clobber the spacing function.
    ...TuscanyTheme,
    spacing: 6,
    shadows: defaultShadows,
    typography: {
      fontFamily: 'helvetica',
      fontWeightRegular: 400,
    },
    palette: {
      ...TuscanyTheme.palette,
      custom: {
        border: {
          active: 'rgba(33, 150, 243, 0.5)',
          inactive: 'rgba(38, 91, 160, 1)',
          disabled: 'rgba(38, 91, 160, 1)',
          recommended: 'rgba(66,108,120, 0.8)',
        },
        success: {
          light: '#52bd8c',
          main: '#009e55',
          dark: '#008640',
          contrastText: '#fff',
        },
        button: {
          light: '#88446c',
          main: '#591841',
          dark: '#2e001b',
          contrastText: '#fff',
        },
        statusIcon: {
          inProgress: '#ffd572',
          sent: '#5cb686',
          archived: '#9c9c9c',
          reopened: '#9c9c9c',
          finalized: '#ca7aa1',
          outdated: '#e35b52',
        },
        gray: {
          light: '#cdcdcd',
          main: '#9c9c9c',
          dark: '#777777',
          background: '#fafafa',
          contrastText: '#fff',
        },
        link: {
          main: '#41636d',
          hover: '#2e4b54',
        },
      },
    },
  },
  // Raw deepmerge over Tuscany's own overrides: ours win per-key, and the
  // pt-ui overrides we do not mention (MuiTabs, MuiChip, ...) survive.
  {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontSize: '0.875rem',
          },
        },
      },
      MuiButton: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            padding: '4px 8px',
            minWidth: 36,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiIconButton: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiCheckbox: {
        defaultProps: {
          color: 'secondary',
        },
      },
      MuiListItem: {
        defaultProps: {
          dense: true,
        },
      },
      MuiTable: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiTextField: {
        defaultProps: {
          margin: 'dense',
        },
      },
      MuiToolbar: {
        defaultProps: {
          variant: 'dense',
        },
      },
      MuiFormControl: {
        defaultProps: {
          margin: 'dense',
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: '1rem',
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            padding: 10,
            borderRadius: '50%',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            padding: '10px 6px',
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          input: {
            padding: '10px 6px',
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            alignItems: 'center !important',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            margin: '10px 0 20px 0',
          },
        },
      },
      MuiIcon: {
        styleOverrides: {
          root: {
            fontSize: '18px !important',
          },
          colorAction: {
            color: '#666666',
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          // Tuscany flattens Fabs with `boxShadow: none` on root and on every
          // state. Put MUI's default elevation back (Fab.js uses shadows[6],
          // and shadows[12] while active).
          root: {
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: defaultShadows[6],
            '&:hover': {
              boxShadow: defaultShadows[6],
            },
            '&.Mui-focusVisible': {
              boxShadow: defaultShadows[6],
            },
            '&:active': {
              boxShadow: defaultShadows[12],
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          body: {
            color: '#777777',
            fontWeight: 600,
          },
        },
      },
      MuiCollapse: {
        styleOverrides: {
          root: {
            width: '100%',
          },
        },
      },
    },
  },
)

export default themeData
